import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { OFFER_TONE, type OfferTone } from "@/lib/offer-tone";

type Tone = OfferTone | "indigo" | "neutral";

const TONE_ACTIVE: Record<Tone, string> = {
  soutien: "border-indigo-600 bg-indigo-600 text-white shadow-sm",
  stage: `border-fuchsia-600 ${OFFER_TONE.stage.solid} shadow-sm`,
  indigo: "border-indigo-600 bg-indigo-600 text-white shadow-sm",
  neutral: "border-gray-700 bg-gray-700 text-white shadow-sm",
};

const TONE_INACTIVE = "border-gray-100 bg-gray-50 text-gray-500 hover:border-indigo-200";

const SIZE = {
  sm: "px-3 py-1.5 text-[9px]",
  md: "px-4 py-2 text-[10px]",
};

interface ToggleChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  active: boolean;
  tone?: Tone;
  size?: "sm" | "md";
  icon?: LucideIcon;
  children: ReactNode;
}

export function ToggleChip({
  active,
  tone = "indigo",
  size = "md",
  icon: Icon,
  children,
  className,
  type = "button",
  ...props
}: ToggleChipProps) {
  return (
    <button
      type={type}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-xl border font-black uppercase tracking-widest transition-all",
        SIZE[size],
        active ? TONE_ACTIVE[tone] : TONE_INACTIVE,
        className,
      )}
      {...props}
    >
      {Icon && <Icon size={size === "sm" ? 11 : 13} />}
      {children}
    </button>
  );
}
